<template>
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <!-- Header -->
    <div class="flex justify-between items-center border-b pb-5" :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-1">
        <h1 class="text-xl font-semibold tracking-tight" :class="isDark ? 'text-white' : 'text-zinc-900'">
          Promotions & Happy Hours
        </h1>
        <p class="text-xs">Gérez vos réductions, codes promos et plages horaires privilégiées</p>
      </div>
      <button @click="openCreateModal" class="px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all shadow-sm"
        :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
        <i class="fas fa-plus mr-2"></i>
        Nouvelle Promotion
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-20">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2" :class="isDark ? 'border-white' : 'border-zinc-900'"></div>
    </div>

    <!-- Promotions List -->
    <div v-else class="grid lg:grid-cols-2 gap-6">
      <div v-for="promo in promotions" :key="promo._id"
        class="border rounded-2xl p-6 space-y-4 transition-all"
        :class="isDark ? 'bg-zinc-900/20 border-zinc-900 hover:border-zinc-800' : 'bg-white border-zinc-200 hover:border-zinc-300'">

        <!-- Header -->
        <div class="flex items-start justify-between">
          <div class="space-y-1 flex-1">
            <div class="flex items-center gap-2">
              <h3 class="text-sm font-semibold" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ promo.nom }}</h3>
              <span v-if="promo.type === 'happy_hour' || promo.happyHour?.actif" class="px-2 py-0.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-[9px] font-bold uppercase rounded">
                Happy Hour
              </span>
            </div>
            <p v-if="promo.description" class="text-xs" :class="isDark ? 'text-zinc-500' : 'text-zinc-600'">
              {{ promo.description }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button @click="togglePromotion(promo)"
              class="w-10 h-5 rounded-full transition-colors relative"
              :class="promo.actif ? 'bg-emerald-600' : (isDark ? 'bg-zinc-800' : 'bg-zinc-200')">
              <div class="w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform"
                :class="promo.actif ? 'right-0.5' : 'left-0.5'"></div>
            </button>
          </div>
        </div>

        <!-- Réduction -->
        <div class="flex items-center gap-3 p-3 rounded-xl border" :class="isDark ? 'bg-zinc-950/60 border-zinc-900/60' : 'bg-zinc-50 border-zinc-100'">
          <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="isDark ? 'bg-purple-950/40' : 'bg-purple-100'">
            <i class="fas fa-tag" :class="isDark ? 'text-purple-400' : 'text-purple-600'"></i>
          </div>
          <div class="flex-1">
            <p class="text-2xl font-bold" :class="isDark ? 'text-white' : 'text-zinc-900'">
              {{ promo.type === 'percentage' ? promo.valeur + '%' : promo.valeur + '€' }}
            </p>
            <p class="text-[10px] uppercase" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
              {{ promo.type === 'percentage' ? 'de réduction' : 'de remise' }}
            </p>
          </div>
          <div v-if="promo.code" class="px-3 py-1 rounded-lg font-mono text-xs font-bold" :class="isDark ? 'bg-zinc-900 text-amber-400' : 'bg-amber-100 text-amber-700'">
            {{ promo.code }}
          </div>
        </div>

        <!-- Happy Hour Details -->
        <div v-if="promo.happyHour?.actif" class="space-y-2 p-3 rounded-xl border" :class="isDark ? 'bg-orange-950/20 border-orange-900/40' : 'bg-orange-50 border-orange-200'">
          <p class="text-xs font-semibold flex items-center gap-2" :class="isDark ? 'text-orange-400' : 'text-orange-700'">
            <i class="fas fa-clock"></i>
            Plages horaires actives
          </p>
          <div class="flex flex-wrap gap-1">
            <span v-for="jour in getJoursLabels(promo.happyHour.jours)" :key="jour"
              class="px-2 py-0.5 rounded text-[10px] font-semibold" :class="isDark ? 'bg-orange-950/40 text-orange-300' : 'bg-orange-200 text-orange-800'">
              {{ jour }}
            </span>
          </div>
          <div class="mt-2 space-y-1">
            <div v-for="(plage, idx) in promo.happyHour.plagesHoraires" :key="idx" class="text-xs flex items-center gap-2">
              <i class="fas fa-arrow-right text-[8px]" :class="isDark ? 'text-orange-500' : 'text-orange-600'"></i>
              <span :class="isDark ? 'text-zinc-400' : 'text-zinc-700'">{{ plage.debut }} - {{ plage.fin }}</span>
            </div>
          </div>
        </div>

        <!-- Période -->
        <div class="flex items-center gap-2 text-xs" :class="isDark ? 'text-zinc-500' : 'text-zinc-600'">
          <i class="fas fa-calendar"></i>
          <span>{{ formatDate(promo.dateDebut) }} → {{ formatDate(promo.dateFin) }}</span>
        </div>

        <!-- Stats -->
        <div class="grid grid-cols-2 gap-3 pt-3 border-t" :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
          <div class="text-center">
            <p class="text-lg font-bold" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ promo.utilisations || 0 }}</p>
            <p class="text-[10px] uppercase" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Utilisations</p>
          </div>
          <div class="text-center">
            <p class="text-lg font-bold" :class="isDark ? 'text-white' : 'text-zinc-900'">
              {{ promo.limiteUtilisationTotale || '∞' }}
            </p>
            <p class="text-[10px] uppercase" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Limite</p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-2 pt-3 border-t" :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
          <button @click="editPromotion(promo)" class="flex-1 px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors"
            :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700'">
            <i class="fas fa-edit mr-1"></i>
            Modifier
          </button>
          <button @click="deletePromotion(promo._id)" class="px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors"
            :class="isDark ? 'bg-red-950/40 hover:bg-red-950/60 text-red-400' : 'bg-red-50 hover:bg-red-100 text-red-600'">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && promotions.length === 0" class="text-center py-20">
      <i class="fas fa-tag text-5xl mb-4" :class="isDark ? 'text-zinc-800' : 'text-zinc-300'"></i>
      <p class="text-sm font-medium mb-2" :class="isDark ? 'text-zinc-500' : 'text-zinc-600'">Aucune promotion pour le moment</p>
      <button @click="openCreateModal" class="mt-4 px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all"
        :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
        Créer votre première promotion
      </button>
    </div>

    <!-- Modal Create/Edit -->
    <teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="closeModal">
        <div class="max-w-2xl w-full max-h-[90vh] overflow-y-auto border rounded-2xl" :class="isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'">
          <div class="sticky top-0 px-6 py-4 border-b flex justify-between items-center" :class="isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'">
            <h2 class="text-lg font-semibold" :class="isDark ? 'text-white' : 'text-zinc-900'">
              {{ editMode ? 'Modifier la promotion' : 'Nouvelle promotion' }}
            </h2>
            <button @click="closeModal" class="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              :class="isDark ? 'hover:bg-zinc-800 text-zinc-500' : 'hover:bg-zinc-100 text-zinc-600'">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="p-6 space-y-6">
            <!-- Nom & Description -->
            <div class="space-y-4">
              <div class="space-y-1">
                <label class="text-[10px] uppercase font-bold tracking-wider">Nom de la promotion *</label>
                <input v-model="form.nom" placeholder="Ex: Happy Hour du jeudi" class="w-full px-4 py-2.5 rounded-lg border text-sm"
                  :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
              </div>
              <div class="space-y-1">
                <label class="text-[10px] uppercase font-bold tracking-wider">Description</label>
                <textarea v-model="form.description" rows="2" placeholder="Description de la promotion" class="w-full px-4 py-2.5 rounded-lg border text-sm resize-none"
                  :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'"></textarea>
              </div>
            </div>

            <!-- Type & Valeur -->
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-1">
                <label class="text-[10px] uppercase font-bold tracking-wider">Type de réduction *</label>
                <select v-model="form.type" class="w-full px-4 py-2.5 rounded-lg border text-sm"
                  :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
                  <option value="percentage">Pourcentage</option>
                  <option value="fixed_amount">Montant fixe</option>
                  <option value="happy_hour">Happy Hour</option>
                </select>
              </div>
              <div class="space-y-1">
                <label class="text-[10px] uppercase font-bold tracking-wider">Valeur *</label>
                <input v-model.number="form.valeur" type="number" min="0" step="1" placeholder="Ex: 20"
                  class="w-full px-4 py-2.5 rounded-lg border text-sm"
                  :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
              </div>
            </div>

            <!-- Code Promo -->
            <div class="space-y-1">
              <label class="text-[10px] uppercase font-bold tracking-wider">Code Promo (optionnel)</label>
              <input v-model="form.code" placeholder="Ex: JEUDI20" maxlength="20" class="w-full px-4 py-2.5 rounded-lg border text-sm font-mono uppercase"
                :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
            </div>

            <!-- Happy Hour Section -->
            <div class="border rounded-xl p-4 space-y-4" :class="isDark ? 'bg-orange-950/10 border-orange-900/40' : 'bg-orange-50 border-orange-200'">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" v-model="form.happyHour.actif" class="w-4 h-4 rounded">
                <span class="text-sm font-semibold" :class="isDark ? 'text-orange-400' : 'text-orange-700'">
                  <i class="fas fa-clock mr-1"></i>
                  Activer le mode Happy Hour
                </span>
              </label>

              <div v-if="form.happyHour.actif" class="space-y-4 pl-6">
                <!-- Jours -->
                <div class="space-y-2">
                  <label class="text-[10px] uppercase font-bold tracking-wider">Jours actifs</label>
                  <div class="flex flex-wrap gap-2">
                    <label v-for="(jour, idx) in joursSemaine" :key="idx" class="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all"
                      :class="form.happyHour.jours.includes(idx)
                        ? (isDark ? 'bg-orange-950/40 border-orange-900/60 text-orange-400' : 'bg-orange-200 border-orange-300 text-orange-800')
                        : (isDark ? 'bg-zinc-950 border-zinc-800 text-zinc-500' : 'bg-white border-zinc-200 text-zinc-600')">
                      <input type="checkbox" :value="idx" v-model="form.happyHour.jours" class="hidden">
                      <span class="text-xs font-semibold">{{ jour }}</span>
                    </label>
                  </div>
                </div>

                <!-- Plages Horaires -->
                <div class="space-y-2">
                  <div class="flex justify-between items-center">
                    <label class="text-[10px] uppercase font-bold tracking-wider">Plages horaires</label>
                    <button @click="addPlageHoraire" type="button" class="text-xs px-3 py-1 rounded-lg"
                      :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-400' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-600'">
                      <i class="fas fa-plus mr-1"></i>
                      Ajouter
                    </button>
                  </div>
                  <div class="space-y-2">
                    <div v-for="(plage, idx) in form.happyHour.plagesHoraires" :key="idx" class="flex items-center gap-2">
                      <input v-model="plage.debut" type="time" class="flex-1 px-3 py-2 rounded-lg border text-sm"
                        :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
                      <span class="text-xs">→</span>
                      <input v-model="plage.fin" type="time" class="flex-1 px-3 py-2 rounded-lg border text-sm"
                        :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
                      <button @click="removePlageHoraire(idx)" type="button" class="w-8 h-8 rounded-lg flex items-center justify-center"
                        :class="isDark ? 'bg-red-950/40 hover:bg-red-950/60 text-red-400' : 'bg-red-50 hover:bg-red-100 text-red-600'">
                        <i class="fas fa-trash text-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Période de validité -->
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-1">
                <label class="text-[10px] uppercase font-bold tracking-wider">Date de début *</label>
                <input v-model="form.dateDebut" type="date" class="w-full px-4 py-2.5 rounded-lg border text-sm"
                  :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
              </div>
              <div class="space-y-1">
                <label class="text-[10px] uppercase font-bold tracking-wider">Date de fin *</label>
                <input v-model="form.dateFin" type="date" class="w-full px-4 py-2.5 rounded-lg border text-sm"
                  :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
              </div>
            </div>

            <!-- Limite utilisation -->
            <div class="space-y-1">
              <label class="text-[10px] uppercase font-bold tracking-wider">Limite d'utilisation (optionnel)</label>
              <input v-model.number="form.limiteUtilisationTotale" type="number" min="0" placeholder="Illimité si vide"
                class="w-full px-4 py-2.5 rounded-lg border text-sm"
                :class="isDark ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-zinc-200'">
            </div>

            <!-- Actions -->
            <div class="flex gap-3 pt-4 border-t" :class="isDark ? 'border-zinc-800' : 'border-zinc-200'">
              <button @click="closeModal" type="button" class="flex-1 px-4 py-3 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors"
                :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700'">
                Annuler
              </button>
              <button @click="savePromotion" type="button" class="flex-1 px-4 py-3 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors"
                :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
                {{ editMode ? 'Modifier' : 'Créer' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import { useTheme } from '@/composables/useTheme'
import api from '@/api'

const { isDark } = useTheme()

const loading = ref(true)
const promotions = ref([])
const showModal = ref(false)
const editMode = ref(false)

const joursSemaine = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

const form = reactive({
  nom: '',
  description: '',
  type: 'percentage',
  valeur: 0,
  code: '',
  dateDebut: '',
  dateFin: '',
  limiteUtilisationTotale: null,
  actif: true,
  happyHour: {
    actif: false,
    jours: [],
    plagesHoraires: []
  }
})

onMounted(async () => {
  await loadPromotions()
})

const loadPromotions = async () => {
  try {
    loading.value = true
    const response = await api.promotions.getAll()
    promotions.value = response.data.data || []
  } catch (error) {
    console.error('Erreur chargement promotions:', error)
    toast.error('Erreur lors du chargement des promotions')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editMode.value = false
  resetForm()
  showModal.value = true
}

const editPromotion = (promo) => {
  editMode.value = true
  form.id = promo._id
  form.nom = promo.nom
  form.description = promo.description || ''
  form.type = promo.type
  form.valeur = promo.valeur
  form.code = promo.code || ''
  form.dateDebut = promo.dateDebut?.substring(0, 10) || ''
  form.dateFin = promo.dateFin?.substring(0, 10) || ''
  form.limiteUtilisationTotale = promo.limiteUtilisationTotale
  form.actif = promo.actif
  form.happyHour = promo.happyHour || { actif: false, jours: [], plagesHoraires: [] }
  showModal.value = true
}

const resetForm = () => {
  form.id = null
  form.nom = ''
  form.description = ''
  form.type = 'percentage'
  form.valeur = 0
  form.code = ''
  form.dateDebut = ''
  form.dateFin = ''
  form.limiteUtilisationTotale = null
  form.actif = true
  form.happyHour = { actif: false, jours: [], plagesHoraires: [] }
}

const closeModal = () => {
  showModal.value = false
  resetForm()
}

const savePromotion = async () => {
  if (!form.nom || !form.valeur || !form.dateDebut || !form.dateFin) {
    toast.error('Veuillez remplir tous les champs obligatoires')
    return
  }

  try {
    if (editMode.value) {
      await api.promotions.update(form.id, form)
      toast.success('Promotion modifiée avec succès')
    } else {
      await api.promotions.create(form)
      toast.success('Promotion créée avec succès')
    }
    await loadPromotions()
    closeModal()
  } catch (error) {
    console.error('Erreur sauvegarde promotion:', error)
    toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde')
  }
}

const togglePromotion = async (promo) => {
  try {
    await api.promotions.toggle(promo._id)
    promo.actif = !promo.actif
    toast.success(`Promotion ${promo.actif ? 'activée' : 'désactivée'}`)
  } catch (error) {
    console.error('Erreur toggle promotion:', error)
    toast.error('Erreur lors du changement de statut')
  }
}

const deletePromotion = async (id) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) return

  try {
    await api.promotions.delete(id)
    toast.success('Promotion supprimée')
    await loadPromotions()
  } catch (error) {
    console.error('Erreur suppression promotion:', error)
    toast.error('Erreur lors de la suppression')
  }
}

const addPlageHoraire = () => {
  form.happyHour.plagesHoraires.push({ debut: '09:00', fin: '18:00' })
}

const removePlageHoraire = (idx) => {
  form.happyHour.plagesHoraires.splice(idx, 1)
}

const getJoursLabels = (jours) => {
  if (!jours || jours.length === 0) return []
  return jours.map(j => joursSemaine[j])
}

const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}
</script>
